/**
 * `<caos-app-home>` — an app's LANDING surface: what is waiting on the reader, with the figures
 * behind it underneath. Opening the AI app lands HERE (not the chat); an "Ask <AI>" button in the
 * hero opens the chat, and the AI workspace tab returns to this surface.
 * (Catalog: Views & filtering › AppHome — an app’s landing surface: what needs attention, gathered.)
 *
 * CAOS-684 — the ORDER of the two halves is the point of this component, not a layout preference.
 * It shipped under CAOS-325 as a dashboard and nothing else, which is the shape CAOS-425 had already
 * ruled out the day before: an app's home says what you need to go and DO, and a dashboard reports
 * what already happened. So the pending region leads and the dashboard is a region OF the surface
 * rather than the whole of it. Neither half is new; which one a reader meets first is.
 *
 * A single self-contained web component (shadow DOM, `--caos-*` tokens only), so it repaints with
 * the theme + brand and appears in the standard component library. Its figures are LIVE and its
 * CONTENT is data: the host sets the `data` property from access-checked reads that measure the
 * whole visible set, and this component decides only how it looks — never what a business is called
 * or which objects it keeps. Sections:
 *   1. Hero — an "Overview" kicker, a display headline (a brand-accented subject word), and
 *      the "Ask <AI>" button pinned right.
 *   2. What is waiting on you — the reader's own unfinished work, longest-waiting first, each row
 *      opening the record. Its empty state is an answer ("nothing is waiting"), not a blank.
 *   3. The dashboard region, under its own heading: the KPI tile strip, a breakdown panel
 *      (horizontal bars, $-weighted) with a Won/Lost footer, and recent activity.
 *
 * Until `data` is set the surface shows a loading state (skeleton tiles + "Loading…" panels); once
 * set it renders the live figures, with honest empty states when the host has nothing to show.
 *
 * The AI name in the button + the accented subject word are brand-aware (read from
 * `--brand-ai-app-name` / `--brand-wordmark`), re-resolved live on a `data-brand` swap.
 *
 * Events out (bubbling + composed — the host owns navigation):
 *   `home-ask`      — the "Ask <AI>" button was chosen (open the chat).
 *   `home-navigate` — detail `{ href }`, a tile/activity deep-link was chosen.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { below } from '../breakpoints.js';
import { makeIcon } from './icons.js';

/** One pipeline-by-stage bucket (label · count / $value). */
export interface AppHomeStage {
  status: string;
  label: string;
  count: number;
  value: number;
}

/** One recent-activity row — a real record with its real status. */
export interface AppHomeActivity {
  id: string;
  label: string;
  sub: string | null;
  status: string | null;
  statusLabel: string;
  /** The status display intent tint (info | success | warning | danger | muted). */
  tone: string;
  href: string;
}

/**
 * CAOS-684 — one thing waiting on the reader: a record of theirs that is still open.
 *
 * Every string arrives already worded, for the same reason the tiles do. What counts as "waiting"
 * and how long it has waited are facts about a workspace's lifecycle and its clock; this component
 * would have to learn both to word them, and it is not allowed to know either.
 */
export interface AppHomePendingItem {
  id: string;
  /** The record's display name. */
  label: string;
  /** What KIND of thing it is, singular ("Order") — the rows mix objects, so each says which it is. */
  objectLabel: string;
  /** Where it sits in its lifecycle, worded ("Presented"). */
  statusLabel: string;
  /** The status display intent tint (info | success | warning | danger | muted). */
  tone: string;
  /** How long it has sat untouched, worded ("Updated 12 days ago"). Empty ⇒ say nothing rather than guess. */
  waitingLabel: string;
  /** Where the row opens. Empty ⇒ nothing puts this object on a tab, so the row is not a control. */
  href: string;
}

/**
 * CAOS-684 — the state of the pending region, as THREE named answers rather than a list that might
 * be empty.
 *
 * An empty list has two completely different causes and they must not render the same. `ok` with no
 * items means the platform looked and the reader genuinely has nothing outstanding. `unreadable`
 * means it looked and could not tell — one bad read of the reader's own work would otherwise draw
 * "Nothing is waiting on you", which is the most confident way this surface could be wrong.
 * `no-lifecycle` means the question does not arise here: nothing in the workspace keeps a status.
 */
export type AppHomePending =
  | { readonly status: 'no-lifecycle' }
  | { readonly status: 'unreadable' }
  | (AppHomePanelHead & { readonly status: 'ok'; readonly items: readonly AppHomePendingItem[] });

/** One headline tile. Its figure arrives already worded — the component lays out, it does not decide. */
export interface AppHomeKpi {
  label: string;
  /** The headline figure, already formatted (`6`, `$3.41M`, `62%`, `—` when there is nothing to say). */
  value: string;
  /** The line under it (`$3.41M open`, `4 won · 1 lost`). Empty is allowed. */
  sub: string;
  /** Where the tile leads. Absent ⇒ the tile is a figure, not a control. */
  href?: string;
}

/** A panel's heading + its optional "see them all" link. */
export interface AppHomePanelHead {
  title: string;
  linkLabel?: string;
  linkHref?: string;
}

/**
 * The live figures the host sets on the `data` property.
 *
 * CAOS-325/356 — nothing here is hard-coded to one business's vocabulary. The host composes it from
 * whatever the workspace actually holds: which objects have a lifecycle, what their statuses mean,
 * and what they add up to. That is why the tiles arrive as a LIST rather than as five fixed slots —
 * a workspace with no orders should not show an "Open Orders" tile reading zero.
 */
export interface AppHomeData {
  /** CAOS-684 — the lead region: what is waiting on the reader. */
  pending: AppHomePending;
  kpis: readonly AppHomeKpi[];
  /** The breakdown panel. `null` ⇒ this workspace has no lifecycle to break down, and the panel is absent. */
  pipeline:
    | (AppHomePanelHead & {
        stages: readonly AppHomeStage[];
        winLoss: { won: { count: number; value: number }; lost: { count: number; value: number } };
      })
    | null;
  recent: AppHomePanelHead & { rows: readonly AppHomeActivity[] };
}

/** $-scale formatter mirroring the reference (`$#.##M` / `$#K` / `$#`). */
export function fmtMoney(v: number): string {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}

/** Map a status display-intent tint to a dot class (the dot palette the sheet defines). */
function toneClass(tone: string): string {
  switch (tone) {
    case 'success': return 'success';
    case 'warning': return 'warning';
    case 'info': return 'info';
    case 'danger': return 'danger';
    case 'accent': return 'accent';
    default: return 'muted';
  }
}

/**
 * The surface's stylesheet, named so a test can read what was AUTHORED. Follows the
 * `BUTTON_STYLES` convention: happy-dom lays nothing out, so a rule about reflow can only be
 * asserted as stylesheet text here, and the geometry it produces has to be measured in a real
 * browser. CAOS-1824's suite says which of the two each of its assertions is.
 */
export const APP_HOME_STYLES = `
  :host { display: block; width: 100%; }
  .overview {
    display: flex;
    flex-direction: column;
    gap: 22px;
    width: 100%;
    /* Single page gutter. The shell content padding is zeroed for the dashboard host, so this
       component owns the ONLY gutter (no shell+component doubling). Responsive tiers step down
       below the large and medium breakpoints (CAOS-590). */
    padding: 48px 48px 80px;
  }
  @media ${below('large')} { .overview { padding: 32px 24px 64px; } }
  @media ${below('medium')} { .overview { padding: 24px 16px 56px; } }

  /* ── Hero ─────────────────────────────────────────────────────────────────── */
  .hero {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: var(--caos-space-4); /* reference hero gap = 16px (landing-surface hero row) */
    flex-wrap: wrap;
  }
  .kicker {
    font-family: var(--caos-font-mono);
    font-size: 10px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
  }
  .title {
    /* Reference parity: 30px/700, no width cap, no wrap, no negative tracking — the reference
       landing surface's headline sits on one line as a flex child. Capping the width or adding
       tracking is what forced CAOS to wrap and waste dashboard height. */
    margin: 8px 0 0;
    font-family: var(--caos-font-display);
    font-size: 30px;
    font-weight: 700;
    line-height: 1.05;
    color: var(--caos-color-text);
  }
  .title .subject { color: var(--caos-color-accent); }
  /* Reference subtitle: inherits body Inter @ 16px, marginTop 6, maxWidth 640 (landing surface). */
  .subtitle { margin: 6px 0 0; font-size: 16px; color: var(--caos-color-text-muted); max-width: 640px; }
  .ask {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 18px;
    border-radius: 8px;
    /* CAOS-1551 — accent-fill under white text: white on accent is 3.60:1, below AA. */
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    border: 1px solid var(--caos-color-accent-fill);
    cursor: pointer;
    font-family: var(--caos-font);
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
    flex-shrink: 0;
    transition: background 0.12s ease;
  }
  /* CAOS-1551 — accent-fill-hover, not accent-strong: accent-strong is lightened on dark and
     this white label measures 4.48:1 there, just under the floor. */
  .ask:hover { background: var(--caos-color-accent-fill-hover); }
  .ask:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .ask svg { width: 16px; height: 16px; }

  /* ── Regions (CAOS-684) ───────────────────────────────────────────────────── */
  /* The surface is two regions now, each with a heading of its own. The dashboard needs one
     because it is no longer the surface: an unlabelled run of tiles under a pending list reads as
     more of the list. */
  .region { display: flex; flex-direction: column; gap: 14px; }
  .region__title {
    margin: 0;
    font-family: var(--caos-font-display);
    font-size: 18px;
    font-weight: 700;
    line-height: 1.2;
    color: var(--caos-color-text);
  }
  /* The waiting row's own line: what kind of thing it is, then how long it has waited. */
  .row__kind { font-size: 12px; color: var(--caos-color-text-muted); white-space: nowrap; }
  .row__waiting { font-size: 12px; color: var(--caos-color-text-muted); white-space: nowrap; flex-shrink: 0; }
  .row__meta { display: inline-flex; align-items: center; gap: 12px; flex-shrink: 0; }

  /* ── KPI tiles ────────────────────────────────────────────────────────────── */
  .kpis {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: var(--caos-space-3);
  }
  .kpi {
    display: flex;
    flex-direction: column;
    /* Reference uses explicit margins (label→value 8px, value→sub 2px), NOT a uniform gap. */
    padding: var(--caos-space-4);
    border: 1px solid var(--caos-color-border);
    border-radius: 10px;
    background: var(--caos-color-surface);
    text-align: left;
    font: inherit;
    cursor: default;
  }
  .kpi.is-link { cursor: pointer; transition: border-color 0.12s ease; }
  .kpi.is-link:hover { border-color: var(--caos-color-accent); }
  .kpi.is-link:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .kpi__label {
    font-family: var(--caos-font-mono);
    font-size: 10px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
  }
  .kpi__value {
    /* Reference (landing surface, line 98): body Inter, 26px, weight 700, plain ink — NOT the display
       face. Value sits 8px below the label. */
    font-family: var(--caos-font);
    font-size: var(--caos-text-kpi);
    font-weight: 700;
    line-height: 1.05;
    margin-top: 8px;
    color: var(--caos-color-text);
    font-variant-numeric: tabular-nums;
  }
  .kpi__sub { margin-top: 2px; font-size: 12px; color: var(--caos-color-text-muted); }

  /* ── Two-column: pipeline + due soon ──────────────────────────────────────── */
  .cols {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 14px; /* reference two-up row gap = 14px (landing surface, line 104) */
  }
  .panel {
    border: 1px solid var(--caos-color-border);
    border-radius: 2px;
    background: var(--caos-color-surface);
    padding: 18px; /* reference pipeline panel padding = 18px (landing surface, line 106) */
  }
  .panel--flush { padding: 0; }
  .panel__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    /* No bottom margin: the pipeline panel's header-to-rows gap comes from the .stages margin-top
       (14px), matching the reference (landing surface, lines 107/111). Flush panels set their own padding. */
    margin-bottom: 0;
  }
  .panel--flush .panel__head { margin-bottom: 0; padding: 14px 16px 8px; }
  .panel__head--icon { justify-content: flex-start; gap: 7px; }
  .panel__title {
    margin: 0;
    font-family: var(--caos-font-mono);
    font-size: 10px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
  }
  .panel__warn { width: 14px; height: 14px; color: var(--caos-color-warning); flex-shrink: 0; }
  .headlink {
    font-family: var(--caos-font-mono);
    font-size: 9px;
    letter-spacing: 0.22em;
    text-transform: uppercase;
    color: var(--caos-color-text-muted);
    background: transparent;
    border: none;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .headlink:hover { color: var(--caos-color-accent); }
  .headlink:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .headlink svg { width: 12px; height: 12px; }

  /* Pipeline bars — two-line: header row (label · count / value) then a full-width track. */
  .stages { display: flex; flex-direction: column; gap: 10px; margin-top: 14px; }
  .stage__head { display: flex; justify-content: space-between; align-items: baseline; gap: var(--caos-space-3); font-size: 12px; margin-bottom: 4px; }
  /* CAOS-1824 — the same shape as '.row', one axis along: three children under space-between, and
     '.stage__value' is nowrap. It was not among the collisions measured at 390px, because the stage
     labels there are short; letting the line wrap costs nothing at any width and removes the way it
     would fail with a longer one. */
  @media ${below('small')} { .stage__head { flex-wrap: wrap; row-gap: 2px; } }
  .stage__label { color: var(--caos-color-text); }
  .stage__count { color: var(--caos-color-text-muted); }
  .stage__value { color: var(--caos-color-text); font-variant-numeric: tabular-nums; white-space: nowrap; }
  .stage__track { height: 7px; border-radius: 999px; background: var(--caos-color-bg); overflow: hidden; }
  .stage__fill { height: 100%; border-radius: 999px; background: var(--caos-color-accent); }

  /* Won/Lost footer */
  .winloss { display: flex; gap: 18px; margin-top: 16px; padding-top: 12px; border-top: 1px solid var(--caos-color-border); }
  .winloss__value { font-weight: 700; margin-top: 4px; font-variant-numeric: tabular-nums; }
  .winloss__value.won { color: var(--caos-color-success); }
  .winloss__value.lost { color: var(--caos-color-text-muted); }

  /* Due-soon / activity rows */
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--caos-space-3);
    width: 100%;
    padding: 10px 16px;
    border: none;
    border-top: 1px solid var(--caos-color-border);
    background: transparent;
    text-align: left;
    font: inherit;
    cursor: default;
  }
  .row.is-link { cursor: pointer; }
  .row.is-link:hover { background: var(--caos-color-bg); } /* reference row hover = --carbon (landing surface, lines 143/166) */
  .row__ident { display: inline-flex; align-items: center; gap: 10px; min-width: 0; }
  .row__id { font-size: 13px; font-weight: 700; color: var(--caos-color-text); white-space: nowrap; }
  .row__cust { font-size: 12px; color: var(--caos-color-text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .row__due { font-size: 12px; color: var(--caos-color-warning); white-space: nowrap; flex-shrink: 0; }
  .row__status { display: inline-flex; align-items: center; gap: 5px; font-size: 11px; color: var(--caos-color-text-muted); white-space: nowrap; flex-shrink: 0; }
  .row__dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .row__dot.success { background: var(--caos-color-success); }
  .row__dot.warning { background: var(--caos-color-warning); }
  .row__dot.accent { background: var(--caos-color-accent); }
  .row__dot.info { background: var(--caos-color-info); }
  .row__dot.danger { background: var(--caos-color-danger); }
  .row__dot.muted { background: var(--caos-color-text-muted); }
  .row__empty { padding: 16px; font-size: 13px; color: var(--caos-color-text-muted); }

  /* ── CAOS-1824: the row reflows instead of drawing its columns on top of each other ────────
     A row is a flex line of two parts: the identity ('.row__ident', which may shrink — it carries
     'min-width: 0') and the trailing meta ('.row__meta' / '.row__status', which may NOT — it
     carries 'flex-shrink: 0'). Both parts hold 'white-space: nowrap' text.

     THAT COMBINATION IS WHAT DREW TEXT OVER TEXT, and it is worth being precise about, because
     "it overflows at phone width" would suggest the wrong fix. At 390px the identity box shrinks
     toward zero, as it is allowed to; its nowrap children cannot shrink with it, so they paint
     OUT of their own box starting at its origin; and the trailing part, which did not shrink, sits
     immediately after that collapsed box. So the name paints straight across the waiting stamp and
     the status. Measured at 390x844 against the labels the audit recorded live: a 35-character
     record name painted over its own "Updated N days ago" stamp by 43x16px, and the row's kind
     label over that same stamp by 37x16px. The strings themselves are on the ticket; this file is
     platform source, and the vocabulary census refuses a customer's business word here.

     Below 'small' the row becomes a column — identity, then meta — so there is nothing to the right
     of the identity to collide with, and the identity itself becomes a column so the kind sits under
     the name rather than after it. The name is then allowed to WRAP rather than truncate: the
     acceptance criterion is that the row is readable, and a 35-character project name is the thing
     a reader most needs. 'overflow-wrap: anywhere' is what keeps a single long token from doing by
     overflow what the nowrap did by overlap, which would be horizontal document overflow instead.

     Desktop is untouched: every rule here is inside the query. */
  @media ${below('small')} {
    .row {
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
    }
    .row__ident {
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      /* The identity is the whole line now, so it may use it. Without this it stays an 'inline-flex'
         sized to its content and the wrap below has nothing to wrap against. */
      width: 100%;
    }
    .row__id {
      white-space: normal;
      overflow-wrap: anywhere;
    }
    /* '.row__cust' truncates on one line at desktop width ('text-overflow: ellipsis'). On its own
       line it has the room to say the whole thing, and an ellipsis here would hide the customer
       name the row exists to identify. */
    .row__cust {
      white-space: normal;
      overflow: visible;
      text-overflow: clip;
      overflow-wrap: anywhere;
    }
    /* The trailing part is its own line and may wrap within it, so a longer stamp or status cannot
       reintroduce the overflow the 'flex-shrink: 0' above is there to prevent at desktop width. */
    .row__meta {
      flex-wrap: wrap;
      row-gap: 2px;
      max-width: 100%;
    }
    /* EVERY REMAINING NOWRAP PART, for the same reason as '.row__id' above and no other.
       Once the row is a column, each of these is an item sized to its own content with no break
       opportunity in it, so a string longer than the line runs off the right edge instead of
       overlapping — trading the overlap for the horizontal overflow the ticket forbids.
         '.row__kind'    is inside '.row__ident', which is now a full-width column.
         '.row__status'  is sometimes a DIRECT child of '.row': the "Recent activity" row appends
                         it bare, so the '.row__meta' rule above never reaches it. This is the one
                         case where the trailing part is not wrapped, and it is why this rule is
                         written against the parts rather than against their container.
         '.row__waiting' is inside '.row__meta', which wraps as a LINE — but wrapping the line does
                         not give a nowrap item inside it anywhere to break.
         '.row__due'     has no template that uses it today. It is listed because it is the same
                         shape, not because a defect was observed through it.
       '.row__status' stays an 'inline-flex' so its tone dot keeps its place beside the first line;
       'max-width: 100%' is what stops the box itself from exceeding the line it sits on. */
    .row__kind,
    .row__status,
    .row__waiting,
    .row__due {
      white-space: normal;
      overflow-wrap: anywhere;
      max-width: 100%;
    }
  }

  /* Loading skeleton — a muted em-dash placeholder for a KPI value / panel body pre-data. */
  .is-loading { color: var(--caos-color-text-muted); }
`;

const sheet = makeSheet(APP_HOME_STYLES);

/**
 * CAOS-602 — the two marks this surface drew for itself, and the whole icon-builder it needed in
 * order to draw them. `arrow-up-right` was drawn independently in the split view as well; two
 * components arriving at the same arrow by different routes is the second icon set, already begun.
 */
const chatIcon = (): SVGSVGElement => makeIcon('message-square');
const arrowIcon = (): SVGSVGElement => makeIcon('arrow-up-right');
export class CaosAppHome extends CaosElement {
  static get observedAttributes(): string[] {
    // CAOS-649 — the hero greets by role, not by name, so a name is not an input: an attribute the
    // surface observed and never drew accepted a value, cost a redraw, and showed nothing.
    return ['user-role'];
  }

  private brandObserver: MutationObserver | null = null;
  private _data: AppHomeData | null = null;

  constructor() {
    super(sheet);
  }

  /** The live metrics feed. Null ⇒ the loading state; set by the host after the metrics read. */
  get data(): AppHomeData | null {
    return this._data;
  }
  set data(v: AppHomeData | null) {
    this._data = v;
    if (this.isConnected) this.render();
  }

  connectedCallback(): void {
    this.brandObserver = new MutationObserver(() => this.render());
    this.brandObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-brand'] });
    this.render();
  }

  disconnectedCallback(): void {
    this.brandObserver?.disconnect();
    this.brandObserver = null;
  }

  attributeChangedCallback(): void {
    this.render();
  }

  /**
   * CAOS-1573 — a brand token, read from the inherited computed style, with NO JS-level fallback.
   *
   * There used to be one, and its comment said it existed "for a non-DOM test env". That is the
   * shape CAOS-794 and CAOS-795 removed from the topbar, and it was wrong here for the same
   * reason: the compiled token floor's own `:root` already carries a real default for both of
   * these, so a deployment that has never customised them still reads properly, and the only
   * thing a fallback in script can add is to make an org's DELIBERATE clearing of its Company
   * Name unexpressible. An org that cleared the name got a bare topbar and a hero that still
   * said "CAOS" — two surfaces disagreeing about one token, which reads as the platform
   * refusing to forget its own name.
   *
   * A test that needs a value supplies the token, which is the arrangement that keeps this
   * honest: a value only ever reaches the screen because something authored it.
   */
  private brandToken(name: string): string {
    return getComputedStyle(this).getPropertyValue(name).trim().replace(/^["']|["']$/g, '').trim();
  }

  private emit(type: string, detail?: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { bubbles: true, composed: true, detail }));
  }

  private navRow(el: HTMLElement, href?: string): void {
    if (!href) return;
    el.classList.add('is-link');
    el.addEventListener('click', () => this.emit('home-navigate', { href }));
  }

  /** A kicker-styled "All orders ↗" deep-link for a panel head — worded by the caller. */
  private headLink(label: string, href: string): HTMLButtonElement {
    const link = document.createElement('button');
    link.type = 'button';
    link.className = 'headlink';
    link.append(document.createTextNode(label), arrowIcon());
    link.addEventListener('click', () => this.emit('home-navigate', { href }));
    return link;
  }

  /** Build a panel head, with its link only when there is somewhere to send the reader. */
  private panelHead(head: AppHomePanelHead): HTMLElement {
    const el = document.createElement('div');
    el.className = 'panel__head';
    const title = document.createElement('h2');
    title.className = 'panel__title';
    title.textContent = head.title;
    el.append(title);
    if (head.linkHref && head.linkLabel) el.append(this.headLink(head.linkLabel, head.linkHref));
    return el;
  }

  /**
   * CAOS-684 — the lead region: what is waiting on the reader.
   *
   * Every way of having no rows gets its own sentence: still loading, nothing here keeps a status,
   * could not tell, and a genuinely clear desk. They read almost the same to a person and mean
   * different things about whether the platform can see their work, so none of them is a blank.
   */
  private pendingRegion(d: AppHomeData | null): HTMLElement {
    const region = document.createElement('section');
    region.className = 'region';
    const title = document.createElement('h2');
    title.className = 'region__title';
    title.textContent = 'What is waiting on you';
    region.append(title);

    const panel = document.createElement('div');
    panel.className = 'panel panel--flush';
    const say = (text: string): void => {
      const line = document.createElement('div');
      line.className = 'row__empty';
      line.textContent = text;
      panel.append(line);
    };

    if (!d) {
      say('Loading…');
    } else if (d.pending.status === 'no-lifecycle') {
      say('Nothing here keeps a status, so there is no unfinished work to gather.');
    } else if (d.pending.status === 'unreadable') {
      say('Your outstanding work could not be read just now.');
    } else {
      panel.append(this.panelHead(d.pending));
      if (d.pending.items.length === 0) {
        say('Nothing is waiting on you.');
      } else {
        for (const item of d.pending.items) {
          const row = document.createElement(item.href ? 'button' : 'div') as HTMLElement;
          if (item.href) (row as HTMLButtonElement).type = 'button';
          row.className = 'row';

          const ident = document.createElement('span');
          ident.className = 'row__ident';
          const name = document.createElement('strong');
          name.className = 'row__id';
          name.textContent = item.label;
          const kind = document.createElement('span');
          kind.className = 'row__kind';
          kind.textContent = item.objectLabel;
          ident.append(name, kind);

          const meta = document.createElement('span');
          meta.className = 'row__meta';
          if (item.waitingLabel) {
            const waiting = document.createElement('span');
            waiting.className = 'row__waiting';
            waiting.textContent = item.waitingLabel;
            meta.append(waiting);
          }
          const status = document.createElement('span');
          status.className = 'row__status';
          const dot = document.createElement('span');
          dot.className = `row__dot ${toneClass(item.tone)}`;
          status.append(dot, document.createTextNode(item.statusLabel));
          meta.append(status);

          row.append(ident, meta);
          this.navRow(row, item.href);
          panel.append(row);
        }
      }
    }
    region.append(panel);
    return region;
  }

  private render(): void {
    // CAOS-664 / CAOS-1573 — both come from the brand tokens the workspace's design system
    // ships, and neither is defaulted here. What an EMPTY one draws is decided separately for
    // each of them below, because the two are not the same kind of name.
    const aiName = this.brandToken('--brand-ai-app-name');
    const subject = this.brandToken('--brand-wordmark');
    const role = this.getAttribute('user-role')?.trim() || 'Admin';
    const d = this._data;

    this.root.replaceChildren();
    const mc = document.createElement('div');
    mc.className = 'mc';

    // ── Hero ──
    const hero = document.createElement('div');
    hero.className = 'hero';
    const heroText = document.createElement('div');
    const kicker = document.createElement('div');
    kicker.className = 'kicker';
    kicker.textContent = `Overview · ${role}`;
    const title = document.createElement('h1');
    title.className = 'title';
    // CAOS-684 — a headline about SEEING, on a surface whose job is what to go and do.
    //
    // CAOS-1573 — the headline is a SENTENCE, which is why an empty wordmark is not simply drawn
    // as nothing the way the topbar's lockup line is. “What needs your attention across .” is not
    // a sentence, and a trailing preposition over a gap reads as a failed render rather than as an
    // org that chose to have no name on screen. So the clause goes with the name: the sentence
    // ends after “attention”, says exactly what it said before, and names nobody.
    // A token holding only spaces counts as cleared, because `brandToken` trims: a Company Name
    // field somebody emptied by selecting the text and pressing space is the same intent as one
    // they cleared, and the alternative is a hero whose sentence ends on an invisible word.
    if (subject) {
      title.append(document.createTextNode('What needs your attention across '));
      const subjectSpan = document.createElement('span');
      subjectSpan.className = 'subject';
      // Rendered exactly as the brand supplies it (CAOS-635) — the same rule every other surface
      // that reads --brand-wordmark already follows (the topbar lockup, sign-in, the hero). This
      // component used to re-case it for the sentence, which is what turned a deployment literally
      // named "CAOS" into "Caos" the moment a reader could see it (the Component Library entry).
      subjectSpan.textContent = subject;
      title.append(subjectSpan, document.createTextNode('.'));
    } else {
      title.append(document.createTextNode('What needs your attention.'));
    }
    const subtitle = document.createElement('p');
    subtitle.className = 'subtitle';
    // CAOS-684 — the subtitle a dashboard had. It described the surface accurately when the surface
    // was only the dashboard; under a pending list it now describes the second half as if it were
    // the whole, which is the exact error this ticket exists to undo.
    subtitle.textContent = 'What is waiting on you, and how things stand behind it.';
    heroText.append(kicker, title, subtitle);

    const ask = document.createElement('button');
    ask.type = 'button';
    ask.className = 'ask';
    // CAOS-1573 — the AI app's name is decided separately from the wordmark, and it lands
    // differently, because this one labels a CONTROL. An unnamed lockup line can simply not be
    // drawn; a button with no name is a button nobody can identify, by sight or by screen reader.
    //
    // So an empty token does NOT put the platform's own brand name back — that would be the same
    // refusal-to-forget this ticket is fixing, and CAOS-769 already took `--brand-ai-app-name` out
    // of the workspace anchor for naming a package the org may not have. It degrades to the
    // generic functional description this component's own documentation already uses for the
    // thing: the assistant. The topbar takes the same route in the equivalent state, labelling a
    // name-less brand logo “Company logo”, on the reasoning that a generic functional description
    // beats no name at all.
    ask.append(chatIcon(), document.createTextNode(aiName ? `Ask ${aiName}` : 'Ask the assistant'));
    ask.addEventListener('click', () => this.emit('home-ask'));

    hero.append(heroText, ask);
    mc.append(hero);

    // ── What is waiting on you (CAOS-684 — the LEAD region) ──
    mc.append(this.pendingRegion(d));

    // ── The dashboard, as a region OF the surface rather than the surface ──
    const dash = document.createElement('section');
    dash.className = 'region';
    const dashTitle = document.createElement('h2');
    dashTitle.className = 'region__title';
    dashTitle.textContent = 'How things stand';
    dash.append(dashTitle);

    // ── KPI tiles (values from live data; a skeleton em-dash pre-data) ──
    const kpis = document.createElement('div');
    kpis.className = 'kpis';
    if (d && d.kpis.length === 0) {
      // The read settled and genuinely found nothing to measure — no object in this workspace keeps
      // a lifecycle worth a tile. That is a real, honest answer (e.g. no sales-shaped app is
      // installed here), not a loading state and not a grid of tiles reading zero, so it is said
      // plainly rather than left as a mysteriously empty strip.
      const empty = document.createElement('div');
      empty.className = 'row__empty';
      empty.textContent = 'Nothing here keeps a status worth measuring yet.';
      kpis.append(empty);
    } else {
      // Pre-data the strip is four placeholder tiles, so the page has its real shape while it loads
      // rather than reflowing under the reader once the figures land.
      const tiles: readonly AppHomeKpi[] = d
        ? d.kpis
        : [0, 1, 2, 3].map(() => ({ label: '', value: '—', sub: '' }));
      for (const k of tiles) {
        const tile = document.createElement(k.href ? 'button' : 'div') as HTMLElement;
        tile.className = 'kpi';
        if (k.href) (tile as HTMLButtonElement).type = 'button';
        const label = document.createElement('div');
        label.className = 'kpi__label';
        label.textContent = k.label;
        const value = document.createElement('div');
        value.className = d ? 'kpi__value' : 'kpi__value is-loading';
        value.textContent = k.value;
        tile.append(label, value);
        const sub = document.createElement('div');
        sub.className = 'kpi__sub';
        sub.textContent = k.sub;
        tile.append(sub);
        this.navRow(tile, k.href);
        kpis.append(tile);
      }
    }
    dash.append(kpis);

    // ── Pipeline by stage (full width — the reference's due-work panel is omitted, no source field) ──
    const pipe = document.createElement('div');
    pipe.className = 'panel';
    pipe.append(this.panelHead(d?.pipeline ?? { title: 'Pipeline by stage' }));

    if (!d) {
      const loading = document.createElement('div');
      loading.className = 'row__empty';
      loading.textContent = 'Loading…';
      pipe.append(loading);
    } else if (d.pipeline === null || d.pipeline.stages.length === 0) {
      // Nothing in this workspace declares a lifecycle worth breaking down. Say so plainly rather
      // than drawing an empty chart that reads as "everything is zero".
      const empty = document.createElement('div');
      empty.className = 'row__empty';
      empty.textContent = 'Nothing to break down yet.';
      pipe.append(empty);
    } else {
      const maxValue = Math.max(...d.pipeline.stages.map((s) => s.value), 1);
      const stages = document.createElement('div');
      stages.className = 'stages';
      for (const s of d.pipeline.stages) {
        const stage = document.createElement('div');
        const head = document.createElement('div');
        head.className = 'stage__head';
        const labelWrap = document.createElement('span');
        const sl = document.createElement('span');
        sl.className = 'stage__label';
        sl.textContent = s.label;
        const sc = document.createElement('span');
        sc.className = 'stage__count';
        sc.textContent = ` · ${s.count}`;
        labelWrap.append(sl, sc);
        const sv = document.createElement('span');
        sv.className = 'stage__value';
        sv.textContent = fmtMoney(s.value);
        head.append(labelWrap, sv);

        const track = document.createElement('div');
        track.className = 'stage__track';
        const fill = document.createElement('div');
        fill.className = 'stage__fill';
        fill.style.width = `${Math.max(2, Math.round((s.value / maxValue) * 100))}%`;
        track.append(fill);

        stage.append(head, track);
        stages.append(stage);
      }
      pipe.append(stages);

      // Won/Lost footer
      const footer = document.createElement('div');
      footer.className = 'winloss';
      const mkWl = (label: string, cls: 'won' | 'lost', data: { count: number; value: number }) => {
        const block = document.createElement('div');
        const lab = document.createElement('div');
        lab.className = 'kicker';
        lab.textContent = label;
        const val = document.createElement('div');
        val.className = `winloss__value ${cls}`;
        val.textContent = `${data.count} · ${fmtMoney(data.value)}`;
        block.append(lab, val);
        return block;
      };
      footer.append(mkWl('Won', 'won', d.pipeline.winLoss.won), mkWl('Lost', 'lost', d.pipeline.winLoss.lost));
      pipe.append(footer);
    }
    dash.append(pipe);

    // ── Recent activity (flush panel — the newest records with their real status) ──
    const act = document.createElement('div');
    act.className = 'panel panel--flush';
    act.append(this.panelHead(d?.recent ?? { title: 'Recent activity' }));
    if (!d) {
      const loading = document.createElement('div');
      loading.className = 'row__empty';
      loading.textContent = 'Loading…';
      act.append(loading);
    } else if (d.recent.rows.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'row__empty';
      empty.textContent = 'No activity yet.';
      act.append(empty);
    } else {
      for (const a of d.recent.rows) {
        const row = document.createElement('button');
        row.type = 'button';
        row.className = 'row';
        const ident = document.createElement('span');
        ident.className = 'row__ident';
        const id = document.createElement('strong');
        id.className = 'row__id';
        id.textContent = a.label;
        const cust = document.createElement('span');
        cust.className = 'row__cust';
        cust.textContent = a.sub ?? '—';
        ident.append(id, cust);
        const status = document.createElement('span');
        status.className = 'row__status';
        const dot = document.createElement('span');
        dot.className = `row__dot ${toneClass(a.tone)}`;
        status.append(dot, document.createTextNode(a.statusLabel));
        row.append(ident, status);
        this.navRow(row, a.href);
        act.append(row);
      }
    }
    dash.append(act);
    mc.append(dash);

    this.root.append(mc);
  }
}

define('caos-app-home', CaosAppHome);

declare global {
  interface HTMLElementTagNameMap {
    'caos-app-home': CaosAppHome;
  }
}
